import { ChangeDetectionStrategy, Component } from '@angular/core';

import { AppLanguage, LanguageService } from '../../providers/language/language.service';

interface LegalSection {
    heading?: string;
    paras?: string[];
    bullets?: string[];
    // Optional trailing paragraphs rendered after the bullet list.
    trailing?: string[];
}

interface LegalPage {
    title: string;
    sections: LegalSection[];
}

@Component({
    selector: 'vsf-terms-page',
    templateUrl: './terms-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsPageComponent {
    constructor(private languageService: LanguageService) {}

    get page(): LegalPage {
        return this.content[this.languageService.currentLanguage];
    }

    private content: Record<AppLanguage, LegalPage> = {
        ar: {
            title: 'الشروط والأحكام – Industore',
            sections: [
                {
                    heading: 'مقدمة',
                    paras: [
                        'مرحبًا بكم في منصة Industore. باستخدامك لهذا الموقع، فإنك توافق على الالتزام بالشروط والأحكام المذكورة أدناه. يرجى قراءة هذه الشروط بعناية قبل استخدام الموقع أو إجراء أي عملية شراء.',
                        'تحتفظ Industore بحق تعديل أو تحديث هذه الشروط في أي وقت دون إشعار مسبق، ويعتبر استمرار استخدام الموقع موافقة ضمنية على أي تحديثات يتم إجراؤها.',
                    ],
                },
                {
                    heading: '1. استخدام الموقع',
                    paras: [
                        'يُسمح باستخدام هذا الموقع لأغراض قانونية فقط، وبما يتوافق مع الأنظمة والقوانين المعمول بها.',
                        'يمنع استخدام الموقع لأي من الأغراض التالية:',
                    ],
                    bullets: [
                        'تقديم معلومات مضللة أو غير صحيحة',
                        'محاولة اختراق أو تعطيل الموقع',
                        'إساءة استخدام خدمات الموقع',
                        'استخدام المحتوى المنشور دون إذن مسبق',
                    ],
                },
                {
                    heading: '2. الحسابات والمعلومات',
                    paras: [
                        'قد يتطلب استخدام بعض خدمات الموقع إنشاء حساب أو تقديم معلومات خاصة بالعميل.',
                        'يتحمل المستخدم مسؤولية:',
                    ],
                    bullets: [
                        'صحة المعلومات المقدمة',
                        'الحفاظ على سرية بيانات الحساب',
                        'جميع الأنشطة التي تتم من خلال حسابه',
                    ],
                    trailing: [
                        'ويحق لـ Industore تعليق أو حذف أي حساب في حال الاشتباه بأي استخدام مخالف.',
                    ],
                },
                {
                    heading: '3. المنتجات والمواصفات',
                    paras: [
                        'تسعى Industore لتقديم معلومات دقيقة حول المنتجات، إلا أن بعض المواصفات أو الصور قد تختلف حسب تحديثات الشركات المصنعة أو اختلاف دفعات الإنتاج.',
                        'قد تكون بعض الصور المعروضة لأغراض توضيحية فقط.',
                        'يتحمل العميل مسؤولية التأكد من توافق المنتج مع الاستخدام أو التطبيق الصناعي المطلوب قبل إتمام عملية الشراء.',
                    ],
                },
                {
                    heading: '4. الأسعار والتوفر',
                    paras: [
                        'جميع الأسعار قابلة للتعديل دون إشعار مسبق.',
                        'توفر المنتجات قد يتغير في أي وقت حسب المخزون أو توفر الموردين.',
                        'يحق لـ Industore رفض أو إلغاء أي طلب في حال:',
                    ],
                    bullets: [
                        'وجود خطأ في السعر',
                        'عدم توفر المنتج',
                        'وجود معلومات ناقصة أو غير صحيحة في الطلب',
                    ],
                },
                {
                    heading: '5. الطلبات وتأكيد الشراء',
                    paras: [
                        'إرسال الطلب عبر الموقع لا يعني قبول الطلب بشكل نهائي.',
                        'يتم تأكيد الطلب بعد مراجعة توفر المنتج ومعلومات الشحن والتواصل مع العميل عند الحاجة.',
                    ],
                },
                {
                    heading: '6. الشحن والتوصيل',
                    paras: [
                        'تتم عمليات الشحن داخل Syria فقط في الوقت الحالي.',
                        'قد تختلف مدة التوصيل حسب:',
                    ],
                    bullets: [
                        'موقع العميل',
                        'نوع المنتج',
                        'توفر المنتج',
                        'طريقة الشحن المطلوبة',
                    ],
                    trailing: [
                        'بعض المنتجات الصناعية أو الثقيلة قد تتطلب ترتيبات شحن خاصة ورسوم إضافية.',
                    ],
                },
                {
                    heading: '7. خدمات التركيب',
                    paras: [
                        'قد تتوفر خدمات تركيب لبعض المنتجات مقابل رسوم منفصلة.',
                        'يتم تحديد تفاصيل التركيب بشكل مستقل حسب نوع المنتج وموقع العميل.',
                        'ولا تتحمل Industore أي مسؤولية عن الأعطال الناتجة عن التركيب غير الصحيح من قبل جهات غير معتمدة.',
                    ],
                },
                {
                    heading: '8. سياسة الإرجاع والاستبدال',
                    paras: [
                        'تخضع جميع طلبات الإرجاع والاستبدال لسياسة الإرجاع المعتمدة لدى Industore.',
                        'بسبب طبيعة المنتجات الصناعية، قد لا تكون بعض المنتجات قابلة للإرجاع أو الاستبدال بعد فتحها أو استخدامها.',
                    ],
                },
                {
                    heading: '9. الضمان',
                    paras: [
                        'الضمان يختلف حسب نوع المنتج والشركة المصنعة.',
                        'بعض المنتجات قد تكون مشمولة بضمان محدود فقط.',
                        'ولا يشمل الضمان:',
                    ],
                    bullets: [
                        'سوء الاستخدام',
                        'التركيب الخاطئ',
                        'الأعطال الناتجة عن التشغيل غير الصحيح',
                        'التلف الناتج عن الإهمال أو التعديلات غير المعتمدة',
                    ],
                },
                {
                    heading: '10. حدود المسؤولية',
                    paras: ['لا تتحمل Industore أي مسؤولية عن:'],
                    bullets: [
                        'الاستخدام غير الصحيح للمنتجات',
                        'الأضرار غير المباشرة أو خسائر الإنتاج',
                        'توقف الأعمال الناتج عن سوء الاستخدام أو التركيب غير الصحيح',
                        'عدم توافق المنتج مع التطبيق المطلوب إذا لم يتم التأكد من ذلك مسبقًا',
                    ],
                },
                {
                    heading: '11. الملكية الفكرية',
                    paras: [
                        'جميع المحتويات الموجودة على الموقع، بما في ذلك النصوص والصور والشعارات والتصاميم، هي ملك لـ Industore أو مرخصة لها، ولا يجوز استخدامها أو إعادة نشرها دون إذن مسبق.',
                    ],
                },
                {
                    heading: '12. التعديلات على الموقع والخدمات',
                    paras: [
                        'تحتفظ Industore بحق تعديل أو إيقاف أي جزء من الموقع أو الخدمات أو المنتجات في أي وقت دون إشعار مسبق.',
                    ],
                },
                {
                    heading: '13. التواصل',
                    paras: [
                        'في حال وجود أي استفسارات حول الشروط والأحكام، يمكن التواصل معنا عبر وسائل التواصل الرسمية الخاصة بـ Industore.',
                    ],
                },
            ],
        },
        en: {
            title: 'Terms & Conditions – Industore',
            sections: [
                {
                    heading: 'Introduction',
                    paras: [
                        'Welcome to the Industore platform. By using this website, you agree to be bound by the terms and conditions set out below. Please read these terms carefully before using the website or making any purchase.',
                        'Industore reserves the right to modify or update these terms at any time without prior notice. Continued use of the website is considered implicit acceptance of any updates made.',
                    ],
                },
                {
                    heading: '1. Use of the Website',
                    paras: [
                        'This website may be used for lawful purposes only, and in compliance with applicable regulations and laws.',
                        'Using the website for any of the following purposes is prohibited:',
                    ],
                    bullets: [
                        'Providing misleading or incorrect information',
                        'Attempting to hack or disrupt the website',
                        'Misusing the website services',
                        'Using published content without prior permission',
                    ],
                },
                {
                    heading: '2. Accounts and Information',
                    paras: [
                        'Using some of the website services may require creating an account or providing customer information.',
                        'The user is responsible for:',
                    ],
                    bullets: [
                        'The accuracy of the information provided',
                        'Keeping account credentials confidential',
                        'All activities carried out through the account',
                    ],
                    trailing: [
                        'Industore has the right to suspend or delete any account if any violating use is suspected.',
                    ],
                },
                {
                    heading: '3. Products and Specifications',
                    paras: [
                        'Industore strives to provide accurate information about products; however, some specifications or images may differ according to manufacturer updates or variations between production batches.',
                        'Some displayed images may be for illustrative purposes only.',
                        'The customer is responsible for ensuring the product is compatible with the intended industrial use or application before completing the purchase.',
                    ],
                },
                {
                    heading: '4. Prices and Availability',
                    paras: [
                        'All prices are subject to change without prior notice.',
                        'Product availability may change at any time depending on stock or supplier availability.',
                        'Industore has the right to refuse or cancel any order in the event of:',
                    ],
                    bullets: [
                        'A pricing error',
                        'Product unavailability',
                        'Missing or incorrect information in the order',
                    ],
                },
                {
                    heading: '5. Orders and Purchase Confirmation',
                    paras: [
                        'Submitting an order through the website does not mean the order is finally accepted.',
                        'The order is confirmed after reviewing product availability and shipping information, and contacting the customer when needed.',
                    ],
                },
                {
                    heading: '6. Shipping and Delivery',
                    paras: [
                        'Shipping is currently carried out within Syria only.',
                        'Delivery time may vary depending on:',
                    ],
                    bullets: [
                        'The customer location',
                        'The product type',
                        'Product availability',
                        'The requested shipping method',
                    ],
                    trailing: [
                        'Some industrial or heavy products may require special shipping arrangements and additional fees.',
                    ],
                },
                {
                    heading: '7. Installation Services',
                    paras: [
                        'Installation services may be available for some products for a separate fee.',
                        'Installation details are determined independently according to the product type and the customer location.',
                        'Industore bears no responsibility for malfunctions resulting from incorrect installation by unauthorized parties.',
                    ],
                },
                {
                    heading: '8. Return and Exchange Policy',
                    paras: [
                        'All return and exchange requests are subject to the return policy approved by Industore.',
                        'Due to the nature of industrial products, some products may not be returnable or exchangeable after being opened or used.',
                    ],
                },
                {
                    heading: '9. Warranty',
                    paras: [
                        'The warranty varies according to the product type and the manufacturer.',
                        'Some products may be covered by a limited warranty only.',
                        'The warranty does not cover:',
                    ],
                    bullets: [
                        'Misuse',
                        'Incorrect installation',
                        'Malfunctions resulting from incorrect operation',
                        'Damage resulting from negligence or unauthorized modifications',
                    ],
                },
                {
                    heading: '10. Limitation of Liability',
                    paras: ['Industore bears no responsibility for:'],
                    bullets: [
                        'Incorrect use of the products',
                        'Indirect damages or production losses',
                        'Business interruption resulting from misuse or incorrect installation',
                        'Product incompatibility with the intended application if not verified in advance',
                    ],
                },
                {
                    heading: '11. Intellectual Property',
                    paras: [
                        'All content on the website, including texts, images, logos, and designs, is owned by or licensed to Industore, and may not be used or republished without prior permission.',
                    ],
                },
                {
                    heading: '12. Changes to the Website and Services',
                    paras: [
                        'Industore reserves the right to modify or discontinue any part of the website, services, or products at any time without prior notice.',
                    ],
                },
                {
                    heading: '13. Contact',
                    paras: [
                        'If you have any questions about these terms and conditions, you can contact us through the official Industore communication channels.',
                    ],
                },
            ],
        },
    };
}
